import { InlineIconButton } from "@/components/ui/pressable";
import { Search, X } from "lucide-react";
import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { Input } from "@/components/ui/input";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

/**
 * In-page module chrome row (search / filters / view / create).
 * Pair with header `PageActions` that only teleports the page title —
 * keep product-wide controls (Ask, notifications, global search) in the top bar.
 */
export const ModuleToolbar = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => (
  <div
    className={cn(
      "flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3",
      className,
    )}
  >
    {children}
  </div>
);

export const ModuleToolbarSpacer = () => <div className="min-w-0 flex-1" />;

export const ModuleToolbarActions = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => (
  <div
    className={cn(
      "flex shrink-0 flex-wrap items-center gap-2 sm:ml-auto sm:justify-end",
      className,
    )}
  >
    {children}
  </div>
);

/** Singular → plural helper for count suffixes in search placeholders. */
export const formatModuleItemCount = (
  total: number | null | undefined,
  singular: string,
  plural?: string,
) => {
  if (typeof total !== "number") return null;
  const label = total === 1 ? singular : (plural ?? `${singular}s`);
  return `${total.toLocaleString()} ${label}`;
};

/**
 * Standard module search field: h-8, icon, optional “· N items” in placeholder.
 * Shows a clear (X) control once the field has text.
 * Use as the leading child of ModuleToolbar (replaces ModuleToolbarSpacer).
 * Pass `trailing` for an end-of-field control (e.g. a filters icon).
 *
 * Outer shell is `flex-1` so actions pin right; the input itself stays
 * `sm:max-w-lg` (Accounts chrome pattern).
 */
export const ModuleSearchField = ({
  value,
  onChange,
  basePlaceholder,
  total,
  itemSingular,
  itemPlural,
  className,
  inputClassName,
  fillHeight = false,
  trailing,
}: {
  value: string;
  onChange: (next: string) => void;
  basePlaceholder: string;
  total?: number | null;
  itemSingular?: string;
  itemPlural?: string;
  className?: string;
  inputClassName?: string;
  /** Stretch input to fill parent height (e.g. KPI strip row). */
  fillHeight?: boolean;
  /** Control at the end of the field (e.g. a filters icon). */
  trailing?: ReactNode;
}) => {
  const isMobile = useIsMobile();
  const countLabel =
    itemSingular != null
      ? formatModuleItemCount(total, itemSingular, itemPlural)
      : null;
  const placeholder = countLabel
    ? `${basePlaceholder} · ${countLabel}`
    : basePlaceholder;
  const ariaLabel = countLabel ? `Search among ${countLabel}` : basePlaceholder;
  const hasValue = value.length > 0;
  const hasTrailing = trailing != null;
  // Reserve the trailing controls' real width (one icon or several) so the
  // placeholder and typed text never run under them.
  const trailingRef = useRef<HTMLDivElement>(null);
  const [trailingWidth, setTrailingWidth] = useState(0);
  useLayoutEffect(() => {
    const element = trailingRef.current;
    if (!element) {
      setTrailingWidth(0);
      return;
    }
    const measure = () => setTrailingWidth(element.offsetWidth);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [hasTrailing]);
  const clearButtonRight = hasTrailing ? trailingWidth + 6 : 8;
  const inputPaddingRight = hasTrailing
    ? trailingWidth + (hasValue ? 32 : 8)
    : hasValue
      ? 36
      : undefined;

  return (
    <div
      className={cn("min-w-0 flex-1", fillHeight && "flex h-full", className)}
    >
      <div
        className={cn(
          "relative w-full",
          fillHeight ? "h-full" : isMobile ? "max-w-none" : "sm:max-w-lg",
        )}
      >
        <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
          aria-label={ariaLabel}
          style={
            inputPaddingRight != null
              ? { paddingRight: inputPaddingRight }
              : undefined
          }
          className={cn(
            fillHeight ? "h-full min-h-[44px]" : isMobile ? "h-11" : "h-8",
            "pl-9",
            isMobile &&
              "rounded-full border-0 bg-black/[0.06] shadow-none dark:bg-white/10",
            inputClassName,
          )}
        />
        {hasValue ? (
          <InlineIconButton
            size="sm"
            className={cn("absolute top-1/2 -translate-y-1/2")}
            style={{ right: clearButtonRight }}
            aria-label="Clear search"
            onClick={() => onChange("")}
          >
            <X className="size-3.5" />
          </InlineIconButton>
        ) : null}
        {hasTrailing ? (
          <div
            ref={trailingRef}
            className="absolute top-1/2 right-1 z-10 -translate-y-1/2"
          >
            {trailing}
          </div>
        ) : null}
      </div>
    </div>
  );
};
